import { AlertTriangle, CalendarClock, SquareStack, UserRoundX } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'

import { ErrorState, SkeletonKpis } from '@/shared/components/data'
import { useI18n, type I18n } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { PortfolioKpis } from '@/shared/types/domain'

import { PARAM } from '../use-portfolio-filters'

export interface KpiRowProps {
  kpis?: PortfolioKpis
  isPending: boolean
  error?: unknown
  onRetry?: () => void
  /** Empresa activa: los tiles la arrastran al link para no perder el contexto. */
  companyId: string | null
}

interface Tile {
  key: string
  label: string
  value: number
  icon: LucideIcon
  to: string
  /** Rojo cuando hay al menos uno. El cero NUNCA se pinta de rojo. */
  alert?: boolean
  hint: string
}

function tiles(kpis: PortfolioKpis, companyId: string | null, t: I18n['t']): Tile[] {
  const company = companyId ? `&${PARAM.company}=${companyId}` : ''

  return [
    {
      key: 'activos',
      label: t('portfolio.kpi.activeProjects.label'),
      value: kpis.active_projects,
      icon: SquareStack,
      to: `/due-soon?horizonte=30${company}`,
      hint: t('portfolio.kpi.activeProjects.hint'),
    },
    {
      key: 'vencidas',
      label: t('portfolio.kpi.overdueTasks.label'),
      value: kpis.overdue_tasks,
      icon: AlertTriangle,
      to: `/due-soon?horizonte=30&estado=vencidas${company}`,
      alert: kpis.overdue_tasks > 0,
      hint: t('portfolio.kpi.overdueTasks.hint'),
    },
    {
      key: 'semana',
      label: t('portfolio.kpi.dueThisWeek.label'),
      value: kpis.due_this_week,
      icon: CalendarClock,
      // `estado=por-vencer` y no sólo el horizonte: el tile dice "próximos 7
      // días" y sin esto aterrizaba en una pantalla que además listaba todo lo
      // ya vencido. Tocar un número tiene que llevar a ese número.
      to: `/due-soon?horizonte=7&estado=por-vencer${company}`,
      hint: t('portfolio.kpi.dueThisWeek.hint'),
    },
    {
      key: 'sin-responsable',
      label: t('portfolio.kpi.withoutOwner.label'),
      value: kpis.projects_without_owner,
      icon: UserRoundX,
      to: `/due-soon?horizonte=30&responsable=sin-asignar${company}`,
      alert: kpis.projects_without_owner > 0,
      hint: t('portfolio.kpi.withoutOwner.hint'),
    },
  ]
}

/** Cuatro numeros y a donde ir cuando alguno molesta. */
export function KpiRow({ kpis, isPending, error, onRetry, companyId }: KpiRowProps) {
  const { t, formatNumber } = useI18n()

  if (isPending) return <SkeletonKpis />
  if (error || !kpis) return <ErrorState error={error} onRetry={onRetry} />

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {tiles(kpis, companyId, t).map((tile) => {
        const Icon = tile.icon
        return (
          <Link
            key={tile.key}
            to={tile.to}
            title={tile.hint}
            className={cn(
              'hairline group flex flex-col gap-1 rounded-[var(--radius-card)] bg-surface-1 p-3',
              'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2',
            )}
          >
            <span className="flex items-center gap-1.5 text-2xs text-text-2">
              <Icon
                size={12}
                aria-hidden="true"
                className={cn(tile.alert ? 'text-overdue' : 'text-text-3')}
              />
              {tile.label}
            </span>
            <span
              className={cn(
                'num text-xl leading-none',
                tile.alert ? 'text-overdue' : 'text-text-1',
              )}
            >
              {formatNumber(tile.value)}
            </span>
            <span className="text-2xs text-text-3">{tile.hint}</span>
          </Link>
        )
      })}
    </div>
  )
}
